import axios, {AxiosError, AxiosResponse, InternalAxiosRequestConfig} from "axios";
import {useAuthStore} from "@store/AuthStore";
import {getValidAccessToken} from "@msal/tokenService";
import {buildRoute} from "@router/RouterUtils";
import {AppRoute} from "@router/RoutingModels";
import {useAppContextStore} from "@store/AppContextStore";

let refreshPromise: Promise<string | null> | null = null;

export const ApiClient = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: 20000,
});

function getHeaderAuthorization(config: InternalAxiosRequestConfig): string | undefined {
    const h: any = config.headers;

    if (h?.get) {
        return h.get("Authorization") ?? h.get("authorization") ?? undefined;
    }

    return h?.Authorization ?? h?.authorization ?? undefined;
}

function setHeaderAuthorization(config: InternalAxiosRequestConfig, value: string) {
    const h: any = config.headers;

    if (h?.set) {
        h.set("Authorization", value);
        return;
    }

    (config.headers as any) = {
        ...(config.headers as any),
        Authorization: value,
    };
}

ApiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const {accessToken} = useAuthStore.getState();
    const {projectId, tenantId, projectTimezone, clientTimezone, language} = useAppContextStore.getState();

    if (tenantId) config.headers.set("Jak-Tenant", tenantId);
    if (projectId) config.headers.set("Jak-Project", projectId);
    if (projectTimezone) config.headers.set("Jak-Default-Timezone", projectTimezone);
    if (clientTimezone) config.headers.set("Jak-Client-Timezone", clientTimezone);
    if (language) config.headers.set("Jak-Language", language);

    if (accessToken) {
        console.log("[API][request] token from memory");
        setHeaderAuthorization(config, `Bearer ${accessToken}`);
        return config;
    }

    const manualAuth = getHeaderAuthorization(config);
    if (manualAuth) {
        return config;
    }

    console.log("[API][request] no token -> getValidAccessToken()");
    const token = await getValidAccessToken(false);
    if (token) {
        setHeaderAuthorization(config, `Bearer ${token}`);
    }

    return config;
});

ApiClient.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest: any = error.config;

        const status = error.response?.status;
        const isAuthError = status === 401 || status === 403;

        if (!isAuthError || originalRequest._retry) return Promise.reject(error);

        originalRequest._retry = true;
        console.warn("[API][response] auth error -> refresh (forceRefresh)");

        try {
            if (!refreshPromise) refreshPromise = getValidAccessToken(true);

            const newToken = await refreshPromise;
            refreshPromise = null;

            if (!newToken) {
                console.warn("[API] refresh failed -> force logout");
                handleForceLogout();
                return Promise.reject(error);
            }

            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;

            console.log("[API] retry original request");
            return ApiClient(originalRequest);
        } catch (err) {
            refreshPromise = null;
            handleForceLogout();
            return Promise.reject(err);
        }
    }
);

function handleForceLogout() {
    console.log("[API] force logout");
    useAuthStore.getState().reset();
    useAppContextStore.getState().reset();
    window.location.href = buildRoute(AppRoute.WELCOME);
}
